// The University of Alabama: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UA's Common Data Set 2025-26 (CDS 2025-26 FINAL 9Dec2025.pdf), linked
//    from the Office of Institutional Research and Assessment's Common Data
//    Set page: C1 applicants, admits and enrollees for Fall 2025 (no
//    residency table), C8 test policy for Fall 2027, C21/C22 early plans.
//    Quirk: G1 is marked "Caution: 2025-26" with the box checked that final
//    2026-2027 costs would come July 1, so the CDS costs are not used.
//  - UA Undergraduate Admissions: Freshman Admissions (Fall 2027 priority
//    deadline, FAQs), Freshman Steps to Enrollment (decision timing),
//    Freshman Requirements, Testing Requirements (the fall 2027 and fall 2028
//    testing changes) and Nursing Admissions (the new nursing process for
//    fall 2027 entry). UA publishes a priority deadline and no final one.
//  - UA Afford, Cost of Attendance 2026-2027 (on-campus budget, main campus,
//    in-state and out-of-state). The page's totals ($35,392 and $60,400)
//    include an $80 loan fee estimate; the rows here leave it out, so the
//    totals are $80 lower.
//  - College Scorecard, school id 100751 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UA Common Data Set 2025-2026',
        url: 'https://oira.ua.edu/new/reports/common-data-set/',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UA Undergraduate Admissions, Freshman Admissions',
        url: 'https://admissions.ua.edu/freshman/',
        read: '2026-10-01',
    },
    steps: {
        label: 'UA Undergraduate Admissions, Freshman Steps to Enrollment',
        url: 'https://admissions.ua.edu/freshman/steps/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UA Undergraduate Admissions, Freshman Requirements',
        url: 'https://admissions.ua.edu/freshman/requirements/',
        read: '2026-10-01',
    },
    testing: {
        label: 'UA Undergraduate Admissions, Testing Requirements',
        url: 'https://admissions.ua.edu/testing-requirements/',
        read: '2026-10-01',
    },
    nursing: {
        label: 'UA Undergraduate Admissions, Nursing Admissions',
        url: 'https://admissions.ua.edu/nursing-admissions/',
        read: '2026-10-01',
    },
    costs: {
        label: 'UA Afford, Cost of Attendance',
        url: 'https://afford.ua.edu/cost/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?100751-The-University-of-Alabama',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-alabama',
    kbId: 'university_of_alabama',
    name: 'The University of Alabama',
    shortName: 'Alabama',
    aliases: ['Bama'],
    location: 'Tuscaloosa, AL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UA brand colors); decoration only.
    branding: {
        primary: '#9E1B32',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_alabama_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 61994,
        admitted: 44124,
        enrolled: 8127,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UA\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UA has no early decision or early action plan. Its December 4 priority deadline is an earlier filing date for admission and scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UA, not to a college. One program now admits first-year students separately:',
        rows: [
            { school: 'Capstone College of Nursing (BSN)', requirement: 'New for fall 2027 entry: first-year students are considered for nursing when admitted to UA, through a college review that weighs your high school GPA, a required ACT or SAT score, and your application. Apply by December 4, 2026. Nursing decisions come in late January 2027, and offers must be accepted by March 1, 2027. Admission to UA does not guarantee a place in nursing.', source: 'nursing' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'December 4' },
        rows: [
            { plan: 'Priority deadline (admission and scholarships)', deadline: 'December 4', decision: 'By late March', binding: false },
        ],
        note: 'UA lists a priority deadline and no final deadline. Applying with all documents by December 4, 2026 ensures consideration for admission and every scholarship, and most freshmen who meet it hear by late March, if not sooner.',
        source: 'steps',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For fall 2027 entry, UA requires an ACT or SAT score only if your cumulative high school GPA is below 3.0; with a 3.0 or higher, sending a score is your choice. Nursing applicants must send a score. There is no minimum score. UA uses your highest section scores across sittings of the same test, and if you enter your own scores on the application, your admission depends on official scores arriving before you enroll.',
        source: 'testing',
        cdsLine: 'The Common Data Set marks the SAT or ACT as considered if submitted for fall 2027 applicants and does not mention the GPA rule above.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Testing', line: 'New for fall 2027 entry: applicants with a cumulative high school GPA below 3.0 must send an ACT or SAT score.', source: 'freshman' },
        { topic: 'Nursing', line: 'New for fall 2027 entry: first-year students are admitted into the BSN program at entry through a college review, instead of applying after their lower-division courses.', source: 'nursing' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Students applying from August 2027 to start in 2028 must all send an ACT or SAT score. UA says there will be no minimum score.', source: 'testing' },
        ],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [12718, 36724] },
            { item: 'Housing (on campus)', amounts: [10000, 10000] },
            { item: 'Food', amounts: [5478, 5478] },
            { item: 'Books', amounts: [700, 700] },
            { item: 'Transportation', amounts: [2352, 3354] },
            { item: 'Other expenses', amounts: [4064, 4064] },
        ],
        totals: [35312, 60320],
        note: 'UA\'s estimate for a full-time, dependent undergraduate on the main campus living on campus. UA\'s own totals add an $80 loan fee estimate, left out here.',
        source: 'costs',
        netPrice: {
            average: 20202,
            byIncome: [
                { income: '$0 to $30,000', amount: 18067 },
                { income: '$30,001 to $48,000', amount: 17442 },
                { income: '$48,001 to $75,000', amount: 17545 },
                { income: '$75,001 to $110,000', amount: 21229 },
                { income: 'Over $110,000', amount: 25653 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UA\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
